/**
 * Other players, in the world: the same people, cars, motorbikes,
 * helicopters and parachutes the player has, moved along their smoothed tracks
 * (`net/tracks.ts`), with a name over each head.
 *
 * One who is down (thrown off, knocked or fallen down, `fall` in their
 * position) falls here as they did in their own game: a ragdoll from the
 * pose last drawn for them, set going as their fall says, kept lying until
 * their game says they are up, and getting up where they stood.
 *
 * Only the nearest `MAX_SHOWN` within `RANGE` are drawn. A body is a slot in
 * the shared crowd, so it costs no draw calls of its own; a car, a
 * helicopter or a canopy is built when first needed and kept for the next
 * player who needs one. Their cars carry no headlight: a light added or
 * removed changes every lit material's shader, and each change is a hitch.
 */

import type { Object3D, Scene } from 'three';
import { CanvasTexture, LinearFilter, SRGBColorSpace, Sprite, SpriteMaterial } from 'three';
import { advancePhase, airPose, gaitPose, jumpPose } from '../core/character/gait.js';
import { startFall } from '../core/character/ragdoll.js';
import type { Track } from '../net/tracks.js';
import type { CharacterCrowd } from './character/crowd.js';
import { lookFromSeed } from './character/crowd.js';
import type { BodyWorld } from './character/fallen.js';
import { Fallen } from './character/fallen.js';
import { createParachute } from './character/parachute.js';
import type { HelicopterModel } from './Helicopter.js';
import { HELI, animateHelicopterLights, createHelicopter, orientHelicopter } from './Helicopter.js';
import type { MotoModel, RiderState } from './Motorbike.js';
import { MOTO, animateMotorbike, createMotorbike, createRiderState, riderPose } from './Motorbike.js';
import type { VehicleModel } from './Vehicle.js';
import { animateVehicle, createVehicle, orientToSlope } from './Vehicle.js';

/** Drawn within this, metres. */
export const RANGE = 1500;
/** At most this many at once: the nearest. */
export const MAX_SHOWN = 24;
/** Past its newest sample this long, a player on foot is on the ground, ms. */
const SILENT_MS = 900;
/** Name tags read out to this far, metres. */
const NAME_RANGE = 250;

export interface RemoteSource {
  tracks: ReadonlyMap<string, Track>;
  players: ReadonlyMap<string, { name: string }>;
}

export interface RemoteContext {
  /** Local clock, ms: `performance.now()`. */
  now: number;
  dt: number;
  night: number;
  /** Chunk-local east, north of a place. */
  toLocal: (lat: number, lon: number) => { east: number; north: number };
  /**
   * Ground height at a scene position (x east, z south), when streamed: the
   * terrain, or a road or bridge deck within a step of `near` — so a player on
   * a bridge stands on it, not on the road beneath.
   */
  ground: (x: number, z: number, near: number) => number | undefined;
  /** Where the viewer is, chunk-local. */
  viewer: { east: number; north: number };
  /** The world as a body meets it, for anyone down. */
  bodies: BodyWorld;
}

interface Shown {
  slot: number;
  phase: number;
  /**
   * How far above our ground their feet usually are, on foot: their game's
   * ground and ours can differ by a little, and an older client sends its
   * eye height. Only a quick rise above this is a jump.
   */
  lift: number;
  clock: number;
  tag: Sprite;
  name: string;
  car?: VehicleModel;
  bike?: MotoModel;
  rider?: RiderState;
  /** On a bike: the heading last frame, and the lean read from how fast it turns. */
  lastYaw?: number;
  lean: number;
  heli?: HelicopterModel;
  canopy?: Object3D;
  /** Down: the body, and a place and where it was in the scene, to move it with the scene's origin. */
  fallen?: Fallen;
  anchor?: { lat: number; lon: number; x: number; z: number };
}

function hash(id: string): number {
  let h = 2166136261;
  for (let i = 0; i < id.length; i++) h = Math.imul(h ^ id.charCodeAt(i), 16777619);
  return h >>> 0;
}

/** A name on a rounded cream label, drawn once. */
function nameTag(name: string): Sprite {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d')!;
  const font = '700 36px system-ui, -apple-system, "Segoe UI", sans-serif';
  ctx.font = font;
  const w = Math.ceil(ctx.measureText(name).width) + 36;
  canvas.width = w;
  canvas.height = 56;
  ctx.font = font;
  ctx.fillStyle = 'rgba(247, 240, 227, 0.92)';
  ctx.beginPath();
  ctx.roundRect(0, 0, w, 56, 18);
  ctx.fill();
  ctx.fillStyle = '#2b2f36';
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'center';
  ctx.fillText(name, w / 2, 30);
  const texture = new CanvasTexture(canvas);
  texture.colorSpace = SRGBColorSpace;
  texture.minFilter = LinearFilter;
  // A fixed size on screen, whatever the distance, and never hidden by the
  // ground it is a hair above.
  const sprite = new Sprite(new SpriteMaterial({ map: texture, sizeAttenuation: false, depthTest: true, transparent: true }));
  sprite.scale.set(0.032 * (w / 56), 0.032, 1);
  sprite.center.set(0.5, 0);
  sprite.renderOrder = 10;
  return sprite;
}

function disposeTag(tag: Sprite): void {
  tag.material.map?.dispose();
  tag.material.dispose();
}

export class RemotePlayers {
  private readonly shown = new Map<string, Shown>();
  private readonly spareCars: VehicleModel[] = [];
  private readonly spareBikes: MotoModel[] = [];
  private readonly spareHelis: HelicopterModel[] = [];
  private readonly spareCanopies: Object3D[] = [];

  constructor(private readonly scene: Scene, private readonly crowd: CharacterCrowd) {}

  /** How many are drawn right now. */
  get count(): number {
    return this.shown.size;
  }

  update(source: RemoteSource, ctx: RemoteContext): void {
    // The nearest within range, by where they are now.
    const candidates: Array<{ id: string; d: number }> = [];
    for (const [id, track] of source.tracks) {
      const last = track.last;
      if (last === undefined) continue;
      const at = ctx.toLocal(last.lat, last.lon);
      const d = Math.hypot(at.east - ctx.viewer.east, at.north - ctx.viewer.north);
      if (d <= RANGE) candidates.push({ id, d });
    }
    candidates.sort((a, b) => a.d - b.d);
    const wanted = new Set(candidates.slice(0, MAX_SHOWN).map(c => c.id));
    for (const id of [...this.shown.keys()]) if (!wanted.has(id)) this.release(id);

    for (const id of wanted) {
      const track = source.tracks.get(id)!;
      const time = ctx.now - track.delay();
      const pose = track.at(time);
      if (pose === undefined) continue;
      // Nothing heard for a while: a background tab stops its game, so its last
      // word can be the top of a jump. Nobody hangs in the air that long.
      const silent = time - (track.last?.t ?? time) > SILENT_MS;
      const name = source.players.get(id)?.name ?? '';
      let s = this.shown.get(id);
      if (s === undefined) {
        const slot = this.crowd.add(lookFromSeed(hash(id)));
        if (slot === undefined) continue;
        const tag = nameTag(name);
        this.scene.add(tag);
        s = { slot, phase: 0, lift: NaN, clock: (hash(id) % 1000) / 100, tag, name, lean: 0 };
        this.shown.set(id, s);
      }
      if (s.name !== name) {
        this.scene.remove(s.tag);
        disposeTag(s.tag);
        s.tag = nameTag(name);
        s.name = name;
        this.scene.add(s.tag);
      }
      this.place(s, pose, ctx, silent);
    }
  }

  private place(s: Shown, pose: NonNullable<ReturnType<Track['at']>>, ctx: RemoteContext, silent: boolean): void {
    s.clock += ctx.dt;
    const at = ctx.toLocal(pose.lat, pose.lon);
    const x = at.east, z = -at.north;
    const ground = ctx.ground(x, z, pose.alt);
    // Heading is clockwise from north; a model's yaw turns its forward, -Z,
    // toward -X: so the one is the other negated.
    const yaw = (-pose.heading * Math.PI) / 180;
    const height = ground === undefined ? 0 : pose.alt - ground;

    if (pose.fall !== undefined && s.fallen === undefined) {
      const fallen = new Fallen(this.crowd, s.slot, pose.fall.kind === 'bars' || pose.fall.kind === 'back');
      startFall(fallen.body, pose.fall);
      s.fallen = fallen;
      s.anchor = { lat: pose.lat, lon: pose.lon, x, z };
    }
    if (s.fallen !== undefined && this.lie(s, pose.fall !== undefined, x, z, ctx)) return;

    this.use(s, pose.mode === 'drive' ? 'car' : pose.mode === 'moto' ? 'bike' : pose.mode === 'heli' ? 'heli' : pose.mode === 'freefall' && pose.climb > -18 && height > 3 ? 'canopy' : undefined);
    let body = { visible: true, y: ground ?? pose.alt, bodyPitch: 0, pose: gaitPose(0, 0, s.clock) };
    let tagY = (ground ?? pose.alt) + 2.2;

    if (pose.mode === 'drive' && s.car !== undefined) {
      const car = s.car;
      const g = ground ?? pose.alt;
      // Pitched and rolled to the ground under it: forward is (fx, fz), right (-fz, fx).
      const fx = -Math.sin(yaw), fz = -Math.cos(yaw);
      const at = (along: number, side: number) => ctx.ground(x + fx * along - fz * side, z + fz * along + fx * side, g) ?? g;
      const forward = (at(1.4, 0) - at(-1.4, 0)) / 2.8;
      const lateral = (at(0, 0.8) - at(0, -0.8)) / 1.6;
      car.root.position.set(x, g, z);
      orientToSlope(car.root, yaw, forward, lateral);
      car.root.updateMatrix();
      animateVehicle(car, { speed: pose.speed, steerAngle: 0, accel: 0, lateralAccel: 0, braking: false, night: ctx.night }, ctx.dt);
      body = { ...body, visible: false };
      tagY = g + 2.4;
    } else if (pose.mode === 'moto' && s.bike !== undefined) {
      // Leaning as far as its turn needs: atan(v·ω/g), from the heading's change.
      const bike = s.bike;
      const g = ground ?? pose.alt;
      const turn = s.lastYaw === undefined || ctx.dt <= 0 ? 0 : Math.atan2(Math.sin(s.lastYaw - yaw), Math.cos(s.lastYaw - yaw)) / ctx.dt;
      s.lastYaw = yaw;
      const lean = Math.max(-0.8, Math.min(0.8, Math.atan((pose.speed * turn) / 9.81)));
      s.lean += (lean - s.lean) * (1 - Math.exp(-ctx.dt * 5));
      const fx = -Math.sin(yaw), fz = -Math.cos(yaw);
      const front = ctx.ground(x - fx * MOTO.frontAxleZ, z - fz * MOTO.frontAxleZ, g) ?? g;
      const rear = ctx.ground(x - fx * MOTO.rearAxleZ, z - fz * MOTO.rearAxleZ, g) ?? g;
      const airborne = height > 0.6;
      s.rider ??= createRiderState();
      const seated = riderPose(s.rider, {
        speed: pose.speed, steerAngle: 0, lean: s.lean, accel: 0, airborne, offRoad: true, wheelie: 0, landing: 0,
      }, ctx.dt);
      bike.root.position.set(x, airborne ? pose.alt : g, z);
      bike.root.rotation.set(0, 0, 0);
      bike.root.rotateY(yaw);
      bike.root.rotateX(Math.atan((front - rear) / MOTO.wheelbase));
      bike.root.rotateZ(-(s.lean + seated.bikeLean));
      bike.root.updateMatrix();
      animateMotorbike(bike, {
        speed: pose.speed, steerAngle: 0, front: airborne ? 0 : MOTO.frontSag, rear: airborne ? 0 : MOTO.rearSag,
        braking: false, night: ctx.night, throttle: 0, airborne,
      }, ctx.dt);
      this.crowd.placeRider(s.slot, bike.root.matrix, seated.pose);
      body = { ...body, visible: false };
      tagY = bike.root.position.y + 2.2;
    } else if (pose.mode === 'heli' && s.heli !== undefined) {
      const heli = s.heli;
      heli.root.position.set(x, pose.alt - HELI.skidHeight, z);
      orientHelicopter(heli.root, yaw, 0, 0);
      const running = height > 0.3 || pose.speed > 0.5;
      if (running) {
        heli.mainRotor.rotation.y += HELI.rotorSpeed * ctx.dt;
        heli.tailRotor.rotation.x += HELI.rotorSpeed * 1.8 * ctx.dt;
      }
      animateHelicopterLights(heli, running, ctx.night, ctx.now / 1000);
      body = { ...body, visible: false };
      tagY = pose.alt + 3.2;
    } else if (pose.mode === 'freefall' && height > 3) {
      if (s.canopy !== undefined) {
        s.canopy.position.set(x, pose.alt + 1.42, z);
        s.canopy.rotation.set(Math.sin(s.clock * 1.4) * 0.04, yaw, Math.sin(s.clock * 0.9) * 0.05);
        body = { ...body, y: pose.alt, pose: airPose('canopy', s.clock) };
      } else {
        body = { ...body, y: pose.alt + 0.9, pose: airPose('freefall', s.clock), bodyPitch: Math.PI / 2 };
      }
      tagY = pose.alt + 2.4;
    } else {
      // On foot: on our ground. Their usual height over it settles slowly while
      // they are not rising or falling; a jump is what rises above that.
      const usual = Math.max(-1, Math.min(2.5, height));
      if (ground !== undefined && Number.isNaN(s.lift)) s.lift = usual;
      else if (ground !== undefined && !silent && Math.abs(pose.climb) < 0.8) s.lift += (usual - s.lift) * Math.min(1, ctx.dt * 1.5);
      const jump = ground === undefined || silent ? 0 : Math.max(0, Math.min(3, height - s.lift));
      if (jump > 0.25) {
        body = { ...body, y: (ground ?? pose.alt) + jump, pose: jumpPose(pose.climb, s.clock) };
        tagY = body.y + 2.2;
      } else {
        const speed = Math.min(12, pose.speed);
        s.phase = advancePhase(s.phase, speed, ctx.dt);
        body = { ...body, pose: gaitPose(speed, s.phase, s.clock) };
      }
    }

    // A rider is already posed on the bike.
    if (s.bike === undefined) this.crowd.place(s.slot, { x, y: body.y, z, yaw, pose: body.pose, bodyPitch: body.bodyPitch, visible: body.visible });
    const d = Math.hypot(at.east - ctx.viewer.east, at.north - ctx.viewer.north);
    s.tag.visible = d < NAME_RANGE && s.name !== '';
    s.tag.position.set(x, tagY, z);
  }

  /**
   * Down: the body tumbling, lying while their game says they are down, and
   * getting up at (x, z), where they stood there. True while it is drawn
   * so; false once up, to be drawn as usual.
   */
  private lie(s: Shown, down: boolean, x: number, z: number, ctx: RemoteContext): boolean {
    const fallen = s.fallen!, a = s.anchor!;
    const here = ctx.toLocal(a.lat, a.lon);
    fallen.shift(here.east - a.x, -here.north - a.z);
    a.x = here.east;
    a.z = -here.north;
    fallen.hold = down;
    fallen.step(ctx.dt, ctx.bodies, s.clock, { x, z });
    if (fallen.stood !== undefined) {
      delete s.fallen;
      delete s.anchor;
      return false;
    }
    this.use(s, undefined);
    fallen.draw();
    const [hx, hy, hz] = fallen.at;
    const d = Math.hypot(hx - ctx.viewer.east, -hz - ctx.viewer.north);
    s.tag.visible = d < NAME_RANGE && s.name !== '';
    s.tag.position.set(hx, hy + 1.2, hz);
    return true;
  }

  /** Give a player the machine their mode needs, and return any other. */
  private use(s: Shown, want: 'car' | 'bike' | 'heli' | 'canopy' | undefined): void {
    if (want !== 'car' && s.car !== undefined) { s.car.root.visible = false; this.spareCars.push(s.car); delete s.car; }
    if (want !== 'bike' && s.bike !== undefined) { s.bike.root.visible = false; this.spareBikes.push(s.bike); delete s.bike; delete s.lastYaw; }
    if (want !== 'heli' && s.heli !== undefined) { s.heli.root.visible = false; this.spareHelis.push(s.heli); delete s.heli; }
    if (want !== 'canopy' && s.canopy !== undefined) { s.canopy.visible = false; this.spareCanopies.push(s.canopy); delete s.canopy; }
    if (want === 'car' && s.car === undefined) {
      s.car = this.spareCars.pop() ?? this.newCar();
      s.car.root.visible = true;
    }
    if (want === 'bike' && s.bike === undefined) {
      s.bike = this.spareBikes.pop() ?? this.newBike();
      s.bike.root.visible = true;
    }
    if (want === 'heli' && s.heli === undefined) {
      s.heli = this.spareHelis.pop() ?? this.newHeli();
      s.heli.root.visible = true;
    }
    if (want === 'canopy' && s.canopy === undefined) {
      s.canopy = this.spareCanopies.pop() ?? this.newCanopy();
      s.canopy.visible = true;
    }
  }

  private newCar(): VehicleModel {
    const car = createVehicle();
    // No beam: see the header.
    for (const beam of car.headlights) beam.parent?.remove(beam, beam.target);
    this.scene.add(car.root);
    return car;
  }

  private newBike(): MotoModel {
    const bike = createMotorbike();
    // No beam, as for the cars.
    bike.headlight.parent?.remove(bike.headlight, bike.headlight.target);
    this.scene.add(bike.root);
    return bike;
  }

  private newHeli(): HelicopterModel {
    const heli = createHelicopter();
    this.scene.add(heli.root);
    return heli;
  }

  private newCanopy(): Object3D {
    const canopy = createParachute();
    this.scene.add(canopy);
    return canopy;
  }

  private release(id: string): void {
    const s = this.shown.get(id);
    if (s === undefined) return;
    this.use(s, undefined);
    this.crowd.remove(s.slot);
    this.scene.remove(s.tag);
    disposeTag(s.tag);
    this.shown.delete(id);
  }
}
